import React, { useCallback, useContext, useEffect, useState } from 'react';
import { style } from 'typestyle';
import Modal from 'react-modal';
import { useTranslation } from 'react-i18next';
import * as styles from './modal.styles';
import PrimaryButton from '../../../core/components/buttons/primary-button.component';
import Column from '../../../core/components/column/column.component';
import { MainAxisAlignment } from '../../../core/components/flex/flex.component';
import Row from '../../../core/components/row/row.component';
import { IsMobileContext } from '../../../core/context/is-mobile';
import { useKeycloak } from '../../authentication/keycloak/KeycloakProvider';
import { getOfficeDeletionImpact, type RetirementImpact } from '../services/offices.service';
import DeletionImpactSummary, { useDeletionImpact } from './deletion-impact-summary.component';

interface Props {
  readonly showModal: boolean;
  readonly officeId?: string;
  readonly officeName: string;
  readonly handleClose: () => void;
  /**
   * Called only once the impact, if any, has been acknowledged. Delete stays locked until a returned promise
   * settles, so a second click cannot send a second deletion - which would fail and report an error for an office
   * that was in fact deleted.
   */
  readonly handleAccept: (acknowledged: boolean) => void | Promise<void>;
}

/**
 * Confirms an office deletion, and says what it costs before it happens.
 *
 * Deleting an office cancels everybody's bookings in it from today onwards, closes its groups, and retires any
 * parking or facility no other office uses - so this dialog states the size of that and asks for an explicit
 * acknowledgement, which the server also insists on.
 */
const DeleteOfficeModal = ({ showModal, officeId, officeName, handleClose, handleAccept }: Props): React.JSX.Element => {
  const isMobile = useContext(IsMobileContext);
  const { t } = useTranslation();
  const keycloak = useKeycloak();

  const load = useCallback(async (): Promise<RetirementImpact | undefined> => {
    if (officeId == null) return undefined;
    const result = await getOfficeDeletionImpact(officeId, keycloak);
    return result?.impact;
  }, [officeId, keycloak]);

  const impactState = useDeletionImpact(showModal, officeId == null ? undefined : load);

  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (showModal) setDeleting(false);
  }, [showModal]);

  return (
    <Modal ariaHideApp={false} isOpen={showModal} onRequestClose={handleClose} style={styles.modalStyle(isMobile)}>
      <Column>
        <span className={style(styles.bodyText)}>
          {t('about-to-remove')} {officeName}
        </span>

        <DeletionImpactSummary state={impactState} />

        <Row itemSpacing={12} mainAxisAlignment={MainAxisAlignment.Stretch}>
          <div className={style(styles.button)}>
            <PrimaryButton
              fullWidth
              disabled={!impactState.confirmable || deleting}
              loading={deleting}
              onClick={async (): Promise<void> => {
                setDeleting(true);
                try {
                  await handleAccept(impactState.impact?.hasImpact ?? false);
                } finally {
                  setDeleting(false);
                }
              }}
            >
              {t('delete')}
            </PrimaryButton>
          </div>
          <div className={style(styles.button)}>
            <PrimaryButton fullWidth onClick={handleClose}>
              {t('cancel')}
            </PrimaryButton>
          </div>
        </Row>
      </Column>
    </Modal>
  );
};

export default DeleteOfficeModal;
